import React, { useState, useEffect } from 'react';
import { useProject } from '../contexts/ProjectContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Plug, CheckCircle, AlertTriangle, Settings, Loader2 } from 'lucide-react';

const integrationIcons = {
  kazilink: '🇰🇪',
  sitedesk: '🇦🇺',
  stripe: '💳',
  flutterwave: '💸',
  whatsapp: '💬',
  sendgrid: '📧'
};

const Integrations = () => {
  const { currentProjectId } = useProject();
  const { api, isAdmin } = useAuth();
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchIntegrations = async () => {
    try {
      const data = await api(`/api/integrations/?project_id=${currentProjectId}`);
      setIntegrations(data);
    } catch (err) {
      console.error('Failed to fetch integrations:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentProjectId) fetchIntegrations();
  }, [currentProjectId]);

  const testConnection = async (integrationId) => {
    try {
      await api(`/api/integrations/${integrationId}/test`, { method: 'POST' });
      toast.success('Connection successful');
      fetchIntegrations();
    } catch (err) {
      toast.error('Connection failed');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'connected': return <Badge className="status-badge success"><CheckCircle className="w-3 h-3" /> Connected</Badge>;
      case 'mock_mode': return <Badge className="status-badge info">Mock Mode</Badge>;
      case 'pending': return <Badge className="status-badge warning"><AlertTriangle className="w-3 h-3" /> Pending</Badge>;
      case 'error': return <Badge className="status-badge error">Error</Badge>;
      default: return <Badge className="status-badge neutral">{status}</Badge>;
    }
  };

  return (
    <div data-testid="integrations-page">
      <div className="mb-8">
        <h1 className="text-2xl lg:text-3xl font-bold tracking-tight">Integrations</h1>
        <p className="text-muted-foreground">Connect external services and APIs</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {integrations.map((integration) => (
            <Card key={integration.integration_id} className="bg-card border-border card-glow">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <span className="text-xl">{integrationIcons[integration.provider] || '🔌'}</span>
                    {integration.name}
                  </CardTitle>
                  {getStatusBadge(integration.status)}
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-sm text-muted-foreground mb-3">
                  <span className="capitalize">{integration.integration_type}</span> • {integration.provider}
                </div>
                {integration.credentials_configured ? (
                  <p className="text-xs text-emerald-400 mb-3">✓ Credentials configured</p>
                ) : (
                  <p className="text-xs text-amber-400 mb-3">⚠ Credentials not configured</p>
                )}
                {isAdmin && (
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => testConnection(integration.integration_id)}
                      data-testid={`test-${integration.integration_id}`}
                    >
                      Test Connection
                    </Button>
                    <Button size="sm" variant="ghost">
                      <Settings className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Card className="bg-card border-border card-glow mt-8">
        <CardHeader>
          <CardTitle className="text-base">Configuration Notes</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>• <strong>KaziLink API / SiteDesk API:</strong> Configure your platform API credentials in Settings</p>
          <p>• <strong>Stripe:</strong> Already configured with test mode key</p>
          <p>• <strong>Flutterwave:</strong> Add your API key in environment variables for live M-Pesa payments</p>
          <p>• <strong>WhatsApp / Email:</strong> Configure provider credentials to enable messaging</p>
          <p className="text-xs text-muted-foreground mt-4">Missing credentials? Integrations work in mock mode until configured.</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default Integrations;
